import type { ReactNode } from "react"
import { Link, useRouter } from "@tanstack/react-router"

import { openProduct } from "~/utils/routerUtils"

interface MarkdownLinkProps {
  href: string
  children: ReactNode
}

export default function MarkdownLink({ href, children }: MarkdownLinkProps) {
  const router = useRouter()

  const productMatch = href.match(/^\/product\/([^/?]+)/)
  if (productMatch) {
    const productId = decodeURIComponent(productMatch[1]!)
    const params = new URLSearchParams(href.split("?")[1] ?? "")
    const variantIds = params.getAll("variantIds")
    return (
      <span
        className="underline cursor-pointer"
        onClick={() => openProduct(productId, variantIds, router)}
      >
        {children}
      </span>
    )
  }

  const collectionMatch = href.match(
    /^\/(?:demand-planning\/(fashion|noos)|seasons-collections)\/([^/?]+)/,
  )
  if (collectionMatch) {
    const collectionType = collectionMatch[1] ?? "fashion"
    const collectionId = decodeURIComponent(collectionMatch[2]!)
    const [, query] = href.split("?")
    const params = new URLSearchParams(query ?? "")
    const search: Record<string, string | boolean> = {}
    if (params.get("isGroup") === "true") search.isGroup = true
    return (
      <Link
        to={
          collectionType === "noos"
            ? "/demand-planning/noos/$collectionId"
            : "/demand-planning/fashion/$collectionId"
        }
        params={{ collectionId }}
        search={search as never}
        className="underline cursor-pointer"
      >
        {children}
      </Link>
    )
  }

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="underline cursor-pointer">
      {children}
    </a>
  )
}

export function renderInlineMarkdownLinks(text: string): ReactNode {
  const linkRegex = /\[([^\]]+)\]\(([^)\s]+)\)/g
  const parts: ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    parts.push(
      <MarkdownLink key={match.index} href={match[2]!}>
        {match[1]!}
      </MarkdownLink>,
    )
    lastIndex = match.index + match[0].length
  }

  if (parts.length === 0) return text
  if (lastIndex < text.length) parts.push(text.slice(lastIndex))
  return <>{parts}</>
}
